<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
   components: {
      VueApexCharts,
   },
   data() {
      return {
         showTable: false,
         // Flag loading untuk menampilkan overlay spinner saat request berjalan.
         loading: false,
      }
   },
   computed: {
      ...mapState({
         kbliPerRisiko: state => state.DashboardOss.kbliPerRisiko,
         tahun: state => state.DashboardOss.tahun,
         refreshTimestamp: state => state.DashboardOss.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      chartData() {
         return this.kbliPerRisiko.data || {}
      },
      chartSeries() {
         return [
            {
               name: 'Jumlah KBLI',
               data: [
                  this.chartData.rendah || 0,
                  this.chartData.menengah_rendah || 0,
                  this.chartData.menengah_tinggi || 0,
                  this.chartData.tinggi || 0,
               ],
            },
         ]
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'
         const gridColor = this.isDark ? '#3b4253' : '#ebe9f1'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: ['#28c76f', '#00cfe8', '#ff9f43', '#ea5455'],
            plotOptions: {
               bar: {
                  borderRadius: 4,
                  columnWidth: '50%',
                  distributed: true,
               },
            },
            dataLabels: {
               enabled: true,
               formatter(val) {
                  return val > 0 ? val.toLocaleString('id-ID') : ''
               },
            },
            grid: {
               borderColor: gridColor,
               strokeDashArray: 4,
            },
            legend: {
               show: false,
            },
            xaxis: {
               categories: ['Rendah', 'Menengah Rendah', 'Menengah Tinggi', 'Tinggi'],
               labels: {
                  style: {
                     colors: textColor,
                  },
               },
               title: {
                  text: 'Tingkat Risiko',
                  style: {
                     color: textColor,
                  },
               },
            },
            yaxis: {
               min: 0,
               forceNiceScale: true,
               title: {
                  text: 'Jumlah KBLI',
                  style: {
                     color: textColor,
                  },
               },
               labels: {
                  style: {
                     colors: [textColor],
                  },
                  formatter: value => Math.round(value).toLocaleString('id-ID'),
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: value => `${value.toLocaleString('id-ID')} KBLI`,
               },
            },
         }
      },
      tableItems() {
         const data = this.kbliPerRisiko.data || {}
         const mapping = [
            { key: 'rendah', label: 'Rendah' },
            { key: 'menengah_rendah', label: 'Menengah Rendah' },
            { key: 'menengah_tinggi', label: 'Menengah Tinggi' },
            { key: 'tinggi', label: 'Tinggi' },
         ]
         return mapping.map(item => ({
            tingkat_risiko: item.label,
            total: data[item.key] || 0,
         }))
      },
      risikoColors() {
         return {
            Rendah: '#28c76f',
            'Menengah Rendah': '#00cfe8',
            'Menengah Tinggi': '#ff9f43',
            Tinggi: '#ea5455',
         }
      },
   },
   watch: {
      tahun(newYear) {
         this.loadData(newYear)
      },
      refreshTimestamp() {
         this.loadData(this.tahun)
      },
   },
   mounted() {
      this.loadData(this.tahun)
   },
   methods: {
      ...mapActions({
         getKbliPerRisiko: 'DashboardOss/getKbliPerRisiko',
      }),
      async loadData(year) {
         this.loading = true
         try {
            await this.getKbliPerRisiko(year)
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      getRiskColor(risiko) {
         return this.risikoColors[risiko] || '#7367f0'
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card class="card-wrapper">
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        KBLI per Tingkat Risiko tahun {{ tahun }}
                     </h4>
                     <small class="text-muted">Distribusi jumlah KBLI berdasarkan tingkat risiko usaha</small>
                  </div>
                  <b-button-group size="sm">
                     <b-button
                        :variant="!showTable ? 'primary' : 'outline-secondary'"
                        @click="showTable = false"
                     >
                        <feather-icon icon="BarChart2Icon" size="14" class="mr-25" />
                        Chart
                     </b-button>
                     <b-button
                        :variant="showTable ? 'primary' : 'outline-secondary'"
                        @click="showTable = true"
                     >
                        <feather-icon icon="TableIcon" size="14" class="mr-25" />
                        Tabel
                     </b-button>
                  </b-button-group>
               </div>

               <!-- Risk Level Legend -->
               <div class="mb-1 d-flex flex-wrap gap-1">
                  <div
                     v-for="(color, risiko) in risikoColors"
                     :key="risiko"
                     class="legend-item d-flex align-items-center mr-1"
                  >
                     <span
                        class="legend-color mr-1"
                        :style="{ background: color }"
                     />
                     <small class="text-muted">{{ risiko }}</small>
                  </div>
               </div>

               <!-- Chart View -->
               <div v-if="!showTable">
                  <vue-apex-charts
                     type="bar"
                     height="300"
                     :options="chartOptions"
                     :series="chartSeries"
                  />
               </div>

               <!-- Table View -->
               <div v-else>
                  <b-table
                     :items="tableItems"
                     :fields="[
                        { key: 'tingkat_risiko', label: 'Tingkat Risiko' },
                        { key: 'total', label: 'Jumlah KBLI' },
                     ]"
                     responsive
                     class="kbli-table"
                  >
                     <template #cell(tingkat_risiko)="data">
                        <span
                           class="risk-badge"
                           :style="{
                              background: getRiskColor(data.item.tingkat_risiko) + '20',
                              color: getRiskColor(data.item.tingkat_risiko),
                           }"
                        >
                           {{ data.item.tingkat_risiko }}
                        </span>
                     </template>
                     <template #cell(total)="data">
                        <span class="font-weight-bolder">
                           {{ data.item.total.toLocaleString('id-ID') }}
                        </span>
                     </template>
                  </b-table>
               </div>
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
}

.summary-card {
   background: #eeedff;
   display: inline-block;
   min-width: 120px;
   transition: background-color 0.2s ease;
}

.summary-value {
   color: #7367f0;
}

.dark-layout .summary-card {
   background: rgba(115, 103, 240, 0.2);
}

.dark-layout .summary-value {
   color: #b7b0ff;
}

.legend-item {
   padding: 4px 8px;
   border-radius: 4px;
   background: #f8f8f8;
   transition: background-color 0.2s ease;
}

.dark-layout .legend-item {
   background: rgba(255, 255, 255, 0.08);
}

.legend-color {
   width: 12px;
   height: 12px;
   border-radius: 3px;
   display: inline-block;
}

.risk-badge {
   padding: 4px 10px;
   border-radius: 6px;
   font-size: 0.85rem;
   font-weight: 500;
}

.kbli-table :deep(.table) {
   margin-bottom: 0;
}

.kbli-table :deep(th) {
   border-top: none;
   font-size: 0.85rem;
   color: #6e6b7b;
}
</style>
